import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import './button.css';
import { cursorAttractor, onClickLocationFeedback, bumpIntakeButton } from './ButtonAnimation';

const Button = React.forwardRef(function Button(props, ref) {
  const {
    className,
    variant = 'contained',
    as: Component = 'button',
    disabled,
    icon,
    iconBackground,
    multiline,
    to,
    showSelectedCount,
    ...rest
  } = props;

  const internalRef = useRef(null);
  const combinedRef = (node) => {
    internalRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  useEffect(() => {
    if (internalRef.current && !['staticlink', 'link', 'textlink', 'cookie-link'].includes(variant)) {
      const cleanup = cursorAttractor(internalRef.current);
      return cleanup;
    }
  }, [variant]);

  useEffect(() => {
    if (variant === 'book-intake' && internalRef.current) {
      bumpIntakeButton(internalRef.current);
    }
  }, [variant, props.children]);

  const buttonClass = `button ${className || ''}
                       ${variant === 'outlined' ? 'outline' : ''}
                       ${variant === 'secondary' ? 'secondary' : ''}
                       ${variant === 'text' ? 'text' : ''}
                       ${variant === 'textlink' ? 'textlink' : ''}
                       ${variant === 'link' ? 'link' : ''}
                       ${variant === 'primary' ? 'contained primary' : ''}
                       ${variant === 'staticlink' ? 'staticlink' : ''}
                       ${variant === 'book-intake' ? 'book-intake-button' : ''}
                       ${disabled ? 'disabled' : ''}
                       ${icon ? 'with-icon' : ''}
                       ${iconBackground ? `icon-bg-${iconBackground}` : ''}
                       ${multiline ? 'multiline' : ''}
                       ${className}`;

  const handleClick = (event) => {
    if (!['link', 'textlink', 'staticlink', 'cookie-link', 'nav-link', 'button-footer-link'].includes(variant.toLowerCase())) {
      onClickLocationFeedback(event);
    }
    if (!disabled && props.onClick) {
      props.onClick(event);
    }
  };

  const buttonContent = (
    <span className="button-content">
      {props.children}
      {showSelectedCount && (
        <span className="selected-count">({props.children.split('(')[1].split(')')[0]})</span>
      )}
    </span>
  );

  const buttonClasses = [
    'button',
    variant === 'outlined' && 'outline',
    variant === 'secondary' && 'secondary',
    variant === 'text' && 'text',
    variant === 'textlink' && 'textlink',
    variant === 'link' && 'link',
    variant === 'nav-link' && 'nav-link',
    variant === 'primary' && 'contained primary',
    variant === 'staticlink' && 'staticlink',
    variant === 'book-intake' && 'book-intake-button',
    variant === 'cookie-link' && 'cookie-link',
    variant === 'button-footer-link' && 'button-footer-link',
    disabled && 'disabled',
    icon && 'with-icon',
    iconBackground && `icon-bg-${iconBackground}`,
    multiline && 'multiline',
    className
  ].filter(Boolean).join(' ');

  if (to) {
    return (
      <Link
        ref={combinedRef}
        to={to}
        className={buttonClasses}
        onClick={handleClick}
        {...rest}
      >
        {buttonContent}
      </Link>
    );
  }

  return (
    <Component
      ref={combinedRef}
      className={buttonClasses}
      disabled={disabled}
      onClick={handleClick}
      {...rest}
    >
      {buttonContent}
    </Component>
  );
});

Button.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    'contained',
    'outlined',
    'secondary',
    'text',
    'textlink',
    'link',
    'nav-link',
    'primary',
    'staticlink',
    'book-intake',
    'cookie-link',
    'button-footer-link'
  ]),
  as: PropTypes.elementType,
  disabled: PropTypes.bool,
  icon: PropTypes.string,
  iconBackground: PropTypes.string,
  children: PropTypes.node,
  multiline: PropTypes.bool,
  to: PropTypes.string,
  showSelectedCount: PropTypes.bool
};

export default Button;
